import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject;

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Fonts } from '@vds-core/typography';
import { withVDSManager, calculateRem } from '@vds-core/utilities';
import { ColorTokens } from '@vds-tokens/color';
import { LayoutTokens } from '@vds-tokens/layout';
import { TypographyTokens } from '@vds-tokens/typography'; // Style properties

var colorDefault = ColorTokens.elements.primary.onlight.value;
var colorInverted = ColorTokens.elements.primary.ondark.value;
var fontweightBold = TypographyTokens.fontweight.bold.value;
var fontweightRegular = TypographyTokens.fontweight.regular.value;
var paddingTopBodyLargeStandard = calculateRem(LayoutTokens.space['4X'].value);
var paddingTopBodySmallStandard = calculateRem(LayoutTokens.space['3X'].value);
var paddingTopBodyLargeCompact = calculateRem(LayoutTokens.space['2X'].value);
var paddingTopBodySmallCompact = calculateRem(LayoutTokens.space['1X'].value);
var paddingBulletBodyLarge = calculateRem(LayoutTokens.space['3X'].value);
var paddingBulletBodySmall = calculateRem(LayoutTokens.space['2X'].value);
var paddingNestedBulletBodyLarge = calculateRem(19);
var paddingNestedBulletBodySmall = calculateRem(14);
var propTypes = {
  /**
   * Represents text within a ListItem.
   */
  children: PropTypes.node,

  /**
   * @ignore Determines the font size, line height, font family and font weight of the List item.
   */
  size: PropTypes.oneOf(['micro', 'bodySmall', 'bodyMedium', 'bodyLarge']),

  /**
   * Determines whether mobile, or desktop fontstacks are used.
   */
  viewport: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),

  /**
   * If provided, used as string that labels the element for accessibility.
   */
  ariaLabel: PropTypes.string,

  /**
   * @ignore
   */
  orderedList: PropTypes.bool,

  /**
   * @ignore
   */
  tabIndex: PropTypes.number,

  /**
   * @ignore
   */
  surface: PropTypes.oneOf(['light', 'dark']),

  /**
   * Determnines the spacing bewtween the list items
   */
  spacing: PropTypes.oneOf(['standard', 'compact']),

  /**
   * @ignore
   */
  calculateLetterSpacing: PropTypes.func
};

var _calculateLetterSpacing = function _calculateLetterSpacing(size, viewport) {
  var letterSpacing = '0px';

  if ((size === 'bodyLarge' || size === 'bodyMedium') && viewport === 'desktop') {
    letterSpacing = TypographyTokens.letterspacing.wide.value;
  }

  return letterSpacing;
};

var defaultProps = {
  orderedList: false,
  size: 'bodyLarge',
  viewport: 'desktop',
  surface: 'light',
  calculateLetterSpacing: _calculateLetterSpacing
};
var StyledLi = /*#__PURE__*/styled.li.withConfig({
  displayName: "StyledLi",
  componentId: "VDS__sc-mxruz8-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", ";\n  color: ", ";\n  outline: none;\n  list-style: none;\n  padding-top: ", ";\n  display: flex;\n\n  > span {\n    flex: 0 1 auto;\n  }\n\n  &:first-child {\n    padding-top: 0;\n  }\n\n  &:before {\n    content: '\u2022';\n    font-weight: ", ";\n    padding-right: ", ";\n    flex-grow: 0;\n  }\n  ul {\n    padding: 0;\n    margin: 0;\n    flex-shrink: 0;\n\n    li {\n      list-style: none;\n\n      &:first-child {\n        padding-top: ", ";\n      }\n\n      &::before {\n        content: '\u2013';\n        padding-left: ", ";\n        font-weight: ", ";\n      }\n    }\n  }\n"], ["\n  ", ";\n  color: ", ";\n  outline: none;\n  list-style: none;\n  padding-top: ", ";\n  display: flex;\n\n  > span {\n    flex: 0 1 auto;\n  }\n\n  &:first-child {\n    padding-top: 0;\n  }\n\n  &:before {\n    content: '\\u2022';\n    font-weight: ", ";\n    padding-right: ", ";\n    flex-grow: 0;\n  }\n  ul {\n    padding: 0;\n    margin: 0;\n    flex-shrink: 0;\n\n    li {\n      list-style: none;\n\n      &:first-child {\n        padding-top: ", ";\n      }\n\n      &::before {\n        content: '\\u2013';\n        padding-left: ", ";\n        font-weight: ", ";\n      }\n    }\n  }\n"])), function (_ref) {
  var getFontStyles = _ref.getFontStyles,
      size = _ref.size,
      viewport = _ref.viewport;
  return getFontStyles(size, viewport);
}, function (_ref2) {
  var surface = _ref2.surface;
  return surface === 'dark' ? colorInverted : colorDefault;
}, function (_ref3) {
  var paddingTop = _ref3.paddingTop;
  return paddingTop;
}, fontweightBold, function (_ref4) {
  var paddingBullet = _ref4.paddingBullet;
  return paddingBullet;
}, function (_ref5) {
  var paddingTop = _ref5.paddingTop;
  return paddingTop;
}, function (_ref6) {
  var size = _ref6.size;
  return size === 'bodyLarge' ? paddingNestedBulletBodyLarge : paddingNestedBulletBodySmall;
}, fontweightRegular);

var ListItem = function ListItem(props) {
  var ariaLabel = props.ariaLabel,
      children = props.children,
      size = props.size,
      vp = props.viewport,
      tabIndex = props.tabIndex,
      surface = props.surface,
      className = props.className,
      calculateLetterSpacing = props.calculateLetterSpacing,
      spacing = props.spacing;
  var viewport = vp === 'mobile' ? 'mobile' : 'desktop';
  var paddingTopStandard = size === 'bodyLarge' ? paddingTopBodyLargeStandard : paddingTopBodySmallStandard;
  var paddingTopCompact = size === 'bodyLarge' ? paddingTopBodyLargeCompact : paddingTopBodySmallCompact;
  var paddingBullet = size === 'bodyLarge' ? paddingBulletBodyLarge : paddingBulletBodySmall;

  var _getFontSize = function _getFontSize() {
    switch (size) {
      case 'micro':
        return TypographyTokens.fontsize.micro[11].value;

      case 'bodySmall':
        return TypographyTokens.fontsize.body[12].value;

      case 'bodyMedium':
        return TypographyTokens.fontsize.body[14].value;

      case 'bodyLarge':
        return TypographyTokens.fontsize.body[16].value;
    }
  };

  var _getLineHeight = function _getLineHeight() {
    switch (size) {
      case 'micro':
        return TypographyTokens.lineheight.micro[16].value;

      case 'bodySmall':
        return TypographyTokens.lineheight.body[16].value;

      case 'bodyMedium':
        return TypographyTokens.lineheight.body[18].value;

      case 'bodyLarge':
        return TypographyTokens.lineheight.body[20].value;
    }
  };

  function _getFontStyles() {
    return "\n    font-family: ".concat(size === 'bodyLarge' || size === 'bodyMedium' ? Fonts.VerizonNHGeDS : Fonts.VerizonNHGeTX, ";\n    font-size: ").concat(calculateRem(_getFontSize()), ";\n      font-weight: ").concat(TypographyTokens.fontweight.regular.value, ";\n      line-height: ").concat(calculateRem(_getLineHeight()), ";\n      letter-spacing: ").concat(calculateRem(calculateLetterSpacing(size, viewport)), ";\n    ");
  }

  return /*#__PURE__*/React.createElement(StyledLi, {
    "aria-label": ariaLabel,
    getFontStyles: _getFontStyles,
    size: size,
    tabIndex: tabIndex,
    viewport: viewport,
    surface: surface,
    className: className,
    paddingTop: spacing === 'compact' ? paddingTopCompact : paddingTopStandard,
    paddingBullet: paddingBullet
  }, /*#__PURE__*/React.createElement("span", null, children));
};

ListItem.propTypes = propTypes;
ListItem.defaultProps = defaultProps;
ListItem.displayName = 'ListItem';
export default withVDSManager(ListItem);